// Cookie/marketing consent record. Persisted in localStorage so a
// returning visitor is not re-prompted; broadcast via a custom
// window event so other components (the reb2b loader in particular)
// can react to the change without coordinating through React
// context. Storage failures (privacy mode, disabled storage) are
// swallowed: when we can't remember the choice, the banner shows
// every visit, but the tracker stays off — fail-closed is the right
// default for marketing cookies.

import { useEffect, useState } from "react";


const STORAGE_KEY = "benecrm_cookie_consent";
const CHANGE_EVENT = "benecrm:consent-changed";


export type ConsentValue = "accepted" | "rejected";


export function getStoredConsent(): ConsentValue | null {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "accepted" || v === "rejected" ? v : null;
  } catch {
    return null;
  }
}


export function setStoredConsent(value: ConsentValue): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Storage disabled — still fire the event so in-tab subscribers
    // update for the duration of the session.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}


export interface UseConsentResult {
  status: ConsentValue | null;
  accept: () => void;
  reject: () => void;
}


export function useConsent(): UseConsentResult {
  const [status, setStatus] = useState<ConsentValue | null>(
    () => getStoredConsent(),
  );

  useEffect(() => {
    function refresh() {
      setStatus(getStoredConsent());
    }
    // ``benecrm:consent-changed`` fires for same-tab updates; the
    // browser's native ``storage`` event covers cross-tab updates so
    // accepting in one tab gates the tracker in every open tab.
    window.addEventListener(CHANGE_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  return {
    status,
    accept: () => setStoredConsent("accepted"),
    reject: () => setStoredConsent("rejected"),
  };
}
